import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { X, ArrowLeft, Images, ChevronLeft, ChevronRight } from 'lucide-react';
import { 
  legendsEveningGallery, 
  kochaniCharityGallery,
  trifonMonumentGallery,
  golfTournamentGallery,
  currentGolfTournamentGallery,
  macedoniaGallery,
  hopeMatchGallery,
  events as allEvents
} from '../data/siteData';


const GalleryPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const currentGolfEvent = allEvents.find(event => event.id === 'event_5');

  // Обединява всичките 6 галерии
  const allGalleries = {
    'hope-match': {
      name: 'hope-match',
      title_bg: 'Мач на надеждата - Бургас',
      title_en: 'Match of Hope - Burgas',
      images: hopeMatchGallery || [],
      coverImage: hopeMatchGallery?.[0]?.url || null
    },
    'macedonia-event': {
      name: 'macedonia-event',
      title_bg: 'България и Северна Македония заедно',
      title_en: 'Bulgaria and North Macedonia Together',
      images: macedoniaGallery || [],
      coverImage: macedoniaGallery?.[0]?.url || null
    },
    'golf-tournament': {
      name: 'golf-tournament',
      title_bg: 'Световни футболни легенди в Банско 2025',
      title_en: 'World Football Legends in Bansko 2025',
      images: golfTournamentGallery || [],
      coverImage: golfTournamentGallery?.[0]?.url || null,
      dayGroups: [
        { title_bg: 'Ден 1', title_en: 'Day 1', images: golfTournamentGallery.slice(0, 13) },
        { title_bg: 'Ден 2', title_en: 'Day 2', images: golfTournamentGallery.slice(13, 46) },
        { title_bg: 'Ден 3', title_en: 'Day 3', images: golfTournamentGallery.slice(46) }
      ]
    },
    'golf-tournament-2026': {
      name: 'golf-tournament-2026',
      title_bg: 'Годишен благотворителен голф турнир 2026',
      title_en: 'Annual Charity Golf Tournament 2026',
      images: currentGolfTournamentGallery || [],
      coverImage: currentGolfTournamentGallery?.[66]?.url || currentGolfEvent?.image || null,
      showWhenEmpty: true,
      dayGroups: [
        { title_bg: 'Ден 1', title_en: 'Day 1', images: currentGolfTournamentGallery.slice(0, 6) },
        { title_bg: 'Ден 2', title_en: 'Day 2', images: currentGolfTournamentGallery.slice(6, 63) },
        { title_bg: 'Ден 3', title_en: 'Day 3', images: currentGolfTournamentGallery.slice(63) }
      ]
    },
    'trifon-monument': {
      name: 'trifon-monument',
      title_bg: 'Вечна слава за Трифон Иванов',
      title_en: 'Eternal Glory for Trifon Ivanov',
      images: trifonMonumentGallery || [],
      coverImage: trifonMonumentGallery?.[0]?.url || null
    },
    'kochani-charity': {
      name: 'kochani-charity',
      title_bg: 'Благотворителен мач "Национали 94" за Кочани',
      title_en: 'Charity Match "Nationals 94" for Kochani',
      images: kochaniCharityGallery || [],
      coverImage: kochaniCharityGallery?.[0]?.url || null
    },
    'legends-evening': {
      name: 'legends-evening',
      title_bg: 'Вечер на легендите – САЩ\'94',
      title_en: 'Legends Evening - USA\'94',
      images: legendsEveningGallery || [],
      coverImage: legendsEveningGallery?.[0]?.url || null
    }
  };

  // Новият голф албум се показва още преди да бъдат добавени снимките.
  const validGalleries = Object.values(allGalleries).filter(
    gallery => gallery.images.length > 0 || (gallery.showWhenEmpty && gallery.coverImage)
  ).sort((a, b) => {
    if (a.name === 'golf-tournament-2026') return -1;
    if (b.name === 'golf-tournament-2026') return 1;
    return 0;
  });

  // Отваря галерия от URL хеша (напр. #hope-match)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && allGalleries[hash]) {
      setSelectedCategory(hash);
    }
  }, [location.hash]);

  const getCategoryName = (catKey) => {
    const gallery = allGalleries[catKey];
    if (gallery) {
      return i18n.language === 'bg' ? gallery.title_bg : gallery.title_en;
    }
    return catKey;
  };

  const filteredImages = selectedCategory 
    ? allGalleries[selectedCategory]?.images || []
    : [];
  const selectedGallery = selectedCategory ? allGalleries[selectedCategory] : null;
  const lightboxImages = selectedGallery?.images || [];
  const selectedImageIndex = selectedImage
    ? lightboxImages.findIndex(image => image.id === selectedImage.id)
    : -1;

  const moveLightbox = (direction) => {
    if (selectedImageIndex < 0 || lightboxImages.length < 2) return;
    const nextIndex = (selectedImageIndex + direction + lightboxImages.length) % lightboxImages.length;
    setSelectedImage(lightboxImages[nextIndex]);
  };

  const renderImageGrid = (images, indexOffset = 0) => (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
      {images.map((image, index) => (
        <button
          type="button"
          key={image.id}
          className="relative overflow-hidden border border-border bg-secondary group cursor-zoom-in aspect-square focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background"
          onClick={() => setSelectedImage(image)}
          data-testid={`gallery-image-${index + indexOffset}`}
          aria-label={i18n.language === 'bg' ? `Отвори снимка ${index + indexOffset + 1}` : `Open photo ${index + indexOffset + 1}`}
          style={{ contentVisibility: 'auto', containIntrinsicSize: '240px' }}
        >
          <img
            src={image.url}
            alt={image.title || 'Gallery image'}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            sizes="(max-width: 767px) 50vw, (max-width: 1279px) 33vw, 25vw"
          />
        </button>
      ))}
    </div>
  );

  // Ако няма галерии, показваме съобщение
  if (validGalleries.length === 0) {
    return (
      <div className="min-h-screen py-32 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-foreground mb-6">
            {t('gallery.title')}
          </h1>
          <p className="text-lg text-muted-foreground">
            {i18n.language === 'bg' ? 'Няма налични галерии' : 'No galleries available'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-foreground mb-6" data-testid="gallery-title">
            {t('gallery.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('gallery.subtitle')}
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {!selectedCategory ? (
            // Show category albums
            <motion.div
              key="categories"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {validGalleries.map((gallery, index) => (
                <motion.div
                  key={gallery.name}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="relative overflow-hidden border border-border group cursor-pointer aspect-[4/3]"
                  onClick={() => setSelectedCategory(gallery.name)}
                  data-testid={`gallery-category-${gallery.name}`}
                >
                  <img
                    src={gallery.coverImage}
                    alt={getCategoryName(gallery.name)}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-heading text-2xl font-bold text-white mb-2">
                      {getCategoryName(gallery.name)}
                    </h3>
                    <div className="flex items-center space-x-2 text-gray-300">
                      <Images className="w-4 h-4" />
                      <span>
                        {gallery.images.length > 0
                          ? `${gallery.images.length} ${i18n.language === 'bg' ? 'снимки' : 'photos'}`
                          : (i18n.language === 'bg' ? 'Снимките предстоят' : 'Photos coming soon')}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            // Show images from selected category
            <motion.div
              key="images"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <button
                onClick={() => setSelectedCategory(null)}
                className="flex items-center space-x-2 text-foreground hover:text-accent transition-colors mb-8 group"
                data-testid="gallery-back-button"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                <span className="font-heading uppercase tracking-wider">
                  {i18n.language === 'bg' ? 'Назад към албуми' : 'Back to albums'}
                </span>
              </button>

              <h2 className="font-heading text-3xl font-bold text-foreground mb-8">
                {getCategoryName(selectedCategory)}
              </h2>

              {selectedGallery?.dayGroups ? (
                <div className="space-y-24 md:space-y-32">
                  {selectedGallery.dayGroups.map((group, groupIndex) => {
                    const indexOffset = selectedGallery.dayGroups
                      .slice(0, groupIndex)
                      .reduce((total, previousGroup) => total + previousGroup.images.length, 0);

                    return (
                      <section
                        key={group.title_en}
                        className="pt-8 md:pt-12 border-t-2 border-border first:border-t-0 first:pt-0"
                      >
                        <div className="mb-12 md:mb-16">
                          <h3 className="inline-flex bg-accent px-6 py-3 md:px-8 md:py-4 font-heading text-4xl md:text-5xl font-bold text-accent-foreground shadow-lg">
                            {i18n.language === 'bg' ? group.title_bg : group.title_en}
                          </h3>
                        </div>
                        {renderImageGrid(group.images, indexOffset)}
                      </section>
                    );
                  })}
                </div>
              ) : filteredImages.length > 0 ? (
                renderImageGrid(filteredImages)
              ) : (
                <div className="border border-border bg-card py-16 px-6 text-center">
                  <Images className="w-12 h-12 text-accent mx-auto mb-4" />
                  <p className="text-xl font-heading font-bold text-foreground mb-2">
                    {i18n.language === 'bg' ? 'Галерията е подготвена' : 'The gallery is ready'}
                  </p>
                  <p className="text-muted-foreground">
                    {i18n.language === 'bg'
                      ? 'Снимките от турнира ще бъдат добавени скоро.'
                      : 'Photos from the tournament will be added soon.'}
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Dialog open={!!selectedImage} onOpenChange={(open) => !open && setSelectedImage(null)}>
        <DialogContent
          showCloseButton={false}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') moveLightbox(-1);
            if (event.key === 'ArrowRight') moveLightbox(1);
          }}
          className="!fixed !inset-0 !left-0 !top-0 !h-[100dvh] !w-screen !max-w-none !translate-x-0 !translate-y-0 p-0 gap-0 bg-transparent border-none shadow-none rounded-none"
        >
          {selectedImage && (
            <div className="relative flex h-[100dvh] w-screen items-center justify-center overflow-hidden bg-neutral-900">
              <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
                <img
                  src={selectedImage.url}
                  alt=""
                  className="h-full w-full scale-110 object-cover opacity-70 blur-3xl"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-black/25" />
              </div>

              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="absolute top-3 right-3 z-30 rounded-full bg-black/70 p-2 text-white hover:bg-accent hover:text-accent-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-white"
                data-testid="close-lightbox"
                aria-label={i18n.language === 'bg' ? 'Затвори снимката' : 'Close image'}
              >
                <X className="w-6 h-6" />
              </button>

              {lightboxImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => moveLightbox(-1)}
                    className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 rounded-full bg-black/60 p-2 md:p-3 text-white hover:bg-accent hover:text-accent-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-white"
                    aria-label={i18n.language === 'bg' ? 'Предишна снимка' : 'Previous image'}
                  >
                    <ChevronLeft className="w-7 h-7 md:w-9 md:h-9" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveLightbox(1)}
                    className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 rounded-full bg-black/60 p-2 md:p-3 text-white hover:bg-accent hover:text-accent-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-white"
                    aria-label={i18n.language === 'bg' ? 'Следваща снимка' : 'Next image'}
                  >
                    <ChevronRight className="w-7 h-7 md:w-9 md:h-9" />
                  </button>
                </>
              )}

              <img
                src={selectedImage.url}
                alt={selectedImage.title || 'Gallery image'}
                className="relative z-10 block max-h-[100dvh] max-w-full w-auto h-auto object-contain sm:max-h-[96dvh] sm:max-w-[92vw]"
              />

            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;